import type { CategoryDecisionTrace } from "./category-decision";
import { FoodCategoryCrosswalkPolicy } from "./policy";
import { evaluateFoodCategoryPolicy, evaluateFoodCategoryPolicyDetails } from "./policy-evaluator";
import {
  normalizedSourceIdMap,
  normalizeUsdaBrandedCategorySourceId,
  normalizeUsdaSrFoundationCategorySourceId,
  normalizeUsdaSurveyCategorySourceId,
} from "./source-id-normalization";

const { usdaBrandedV1, usdaSrFoundationV1, usdaSurveyV1 } =
  FoodCategoryCrosswalkPolicy.sourceLensPolicies;

function normalizeFamily(value: string | null | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

export async function resolveUsdaSurveyCategoryKeys(wweiaFoodCategoryDescription?: string | null) {
  const family = normalizeFamily(wweiaFoodCategoryDescription);
  if (!family) {
    return [];
  }

  return evaluateFoodCategoryPolicy({
    sourceIds: normalizedSourceIdMap([family], normalizeUsdaSurveyCategorySourceId).sourceIds,
    highRiskOverrideSourceIds: usdaSurveyV1.highRiskOverrideSourceIds,
    rules: usdaSurveyV1.rules,
  });
}

export async function resolveUsdaSrFoundationCategoryKeys(foodCategoryDescription?: string | null) {
  const family = normalizeFamily(foodCategoryDescription);
  if (!family) {
    return [];
  }

  return evaluateFoodCategoryPolicy({
    sourceIds: normalizedSourceIdMap([family], normalizeUsdaSrFoundationCategorySourceId).sourceIds,
    highRiskOverrideSourceIds: usdaSrFoundationV1.highRiskOverrideSourceIds,
    rules: usdaSrFoundationV1.rules,
  });
}

export async function resolveUsdaBrandedCategoryKeys(brandedFoodCategory?: string | null) {
  const family = normalizeFamily(brandedFoodCategory);
  if (!family) {
    return [];
  }

  return evaluateFoodCategoryPolicy({
    sourceIds: normalizedSourceIdMap([family], normalizeUsdaBrandedCategorySourceId).sourceIds,
    highRiskOverrideSourceIds: usdaBrandedV1.highRiskOverrideSourceIds,
    rules: usdaBrandedV1.rules,
  });
}

function resolveUsdaBrandedFoodCategoryDecision(args: {
  brandedFoodCategory?: string | null;
  productName?: string | null;
  ingredientsText?: string | null;
}): CategoryDecisionTrace {
  const family = args.brandedFoodCategory?.trim() ?? "";
  const { sourceIds, sourceLabelById } = normalizedSourceIdMap(
    family ? [family] : [],
    normalizeUsdaBrandedCategorySourceId,
  );
  const familyForSourceId = (sourceId: string) => sourceLabelById.get(sourceId) ?? sourceId;
  const evaluation = evaluateFoodCategoryPolicyDetails({
    sourceIds,
    highRiskOverrideSourceIds: usdaBrandedV1.highRiskOverrideSourceIds,
    rules: usdaBrandedV1.rules,
    textInputs: [args.productName, args.ingredientsText],
    textRefinementRules: usdaBrandedV1.textRefinementRules,
  });

  let strategy: CategoryDecisionTrace["strategy"] = "unresolved";
  const finalCategoryKey = evaluation.orderedCategoryKeys[0] ?? null;
  const reasons: string[] = [];

  if (evaluation.textRefinement?.categoryKey === finalCategoryKey) {
    strategy = "override";
    reasons.push(evaluation.textRefinement.reason);
  } else if (finalCategoryKey && evaluation.directCategoryKeys.includes(finalCategoryKey)) {
    strategy = "direct";
    reasons.push("USDA branded source policy found direct category match");
  } else if (
    finalCategoryKey &&
    evaluation.inferredPreferredCategoryKeys.includes(finalCategoryKey)
  ) {
    strategy = "preferred";
    reasons.push("USDA branded source policy inferred preferred category");
  } else if (finalCategoryKey && evaluation.fallbackCategoryKeys.includes(finalCategoryKey)) {
    strategy = "fallback";
    reasons.push("USDA branded source policy used fallback category");
  } else {
    reasons.push("no direct, preferred, or fallback category found");
  }

  return {
    source: "usda-branded",
    inputSignals: {
      brandedFoodCategory: args.brandedFoodCategory ?? null,
      productName: args.productName ?? null,
      ingredientsText: args.ingredientsText ?? null,
    },
    baseOrderedCategoryKeys: evaluation.orderedCategoryKeys,
    finalCategoryKey,
    finalOrderedCategoryKeys: evaluation.orderedCategoryKeys,
    strategy,
    usedFallback: strategy === "fallback",
    highRiskSignals: evaluation.highRiskOverrideSourceIds.map(familyForSourceId),
    unmatchedSignals: evaluation.unmatchedSourceIds.map(familyForSourceId),
    reasons,
  };
}

export async function resolveUsdaBrandedFoodCategoryKeys(args: {
  brandedFoodCategory?: string | null;
  productName?: string | null;
  ingredientsText?: string | null;
}) {
  return resolveUsdaBrandedFoodCategoryDecision(args).finalOrderedCategoryKeys;
}
